import React, { useEffect, useState } from "react";
import { api, fmtUSD } from "@/lib/api";
import { ExternalLink, Zap, X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const FALLBACK = "data:image/svg+xml;utf8," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400">
    <rect width="400" height="400" fill="#141008"/>
    <rect x="40" y="50" width="320" height="240" rx="12" fill="#0A0906" stroke="#FFAB00" stroke-width="3"/>
    <g font-family="monospace" fill="#FFAB00">
      <text x="60" y="90" font-size="22" font-weight="700">QUOTRON</text>
      <text x="60" y="130" font-size="14">PRICE  +44.44%</text>
      <text x="60" y="155" font-size="14">VOL    44.44M</text>
      <path d="M60 240 L110 220 L140 230 L180 190 L220 200 L260 170 L300 140 L340 120"
            stroke="#FFC940" stroke-width="3" fill="none"/>
    </g>
  </svg>`
);

function Thumb({ q, onClick }) {
  const [err, setErr] = useState(false);
  const src = err ? FALLBACK : q.image_url;
  return (
    <button
      onClick={onClick}
      data-testid={`quotron-card-${q.token_id}`}
      className="group text-left rounded-lg border border-[#3A2E1C] bg-[#141008] overflow-hidden hover:border-[#FFAB00]/60 hover:shadow-[0_0_30px_rgba(255,171,0,0.2)] transition-all"
    >
      <div className="aspect-square overflow-hidden bg-[#0A0906] relative">
        <img
          src={src}
          alt={`Quotron #${q.token_id}`}
          onError={() => setErr(true)}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full border border-[#FFAB00]/40 bg-[#0A0906]/80 px-2 py-0.5 text-[10px] mono uppercase tracking-widest text-[#FFC940] backdrop-blur">
          <Zap className="w-3 h-3" /> hardwired
        </div>
        {q.floor && (
          <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full border border-[#FFAB00]/40 bg-[#0A0906]/80 px-2 py-0.5 text-[10px] mono uppercase tracking-widest text-[#4FFF4F] backdrop-blur">
            ${q.floor}
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between">
          <div className="font-display text-xl tracking-tight">#{q.token_id}</div>
          <span className="text-[10px] uppercase tracking-widest text-[#FFC940] bg-[#1E1810] border border-[#3A2E1C] px-2 py-0.5 rounded-full">T{q.tier}</span>
        </div>
        <div className="text-xs text-stone-500 mt-0.5 mono truncate">{q.resolved_name || q.tier_name || ""}</div>
      </div>
    </button>
  );
}

function Modal({ q, onClose }) {
  const [attr, setAttr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    if (!q) return;
    setLoading(true); setAttr(null);
    api.get(`/quotrons/${q.id}/attribution`).then((r) => setAttr(r.data)).catch(() => {})
      .finally(() => setLoading(false));
  }, [q]);

  if (!q) return null;

  return (
    <Dialog open={!!q} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0A0906] border-[#3A2E1C] text-stone-100 max-w-2xl p-0 overflow-hidden">
        <DialogTitle className="sr-only">QUOTRON #{q.token_id}</DialogTitle>
        <button
          onClick={onClose}
          data-testid="quotron-modal-close"
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-md border border-[#3A2E1C] bg-[#141008] inline-flex items-center justify-center hover:border-[#FFAB00]/60"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="grid md:grid-cols-5 gap-0">
          <div className="md:col-span-2 bg-[#141008]">
            <img
              src={imgErr ? FALLBACK : q.image_url}
              alt={`Quotron #${q.token_id}`}
              onError={() => setImgErr(true)}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="md:col-span-3 p-6">
            <div className="text-[11px] uppercase tracking-widest text-[#FFAB00]">hardwired · Robinhood Chain</div>
            <div className="font-display text-4xl mt-1">QUOTRON #{q.token_id}</div>
            <div className="text-sm text-stone-400 mt-1 mono">
              T{q.tier} · {q.resolved_name ? "" : q.tier_name} {q.meta_tier || ""}
            </div>
            {q.description && (
              <p className="mt-3 text-sm text-stone-300 leading-relaxed">{q.description}</p>
            )}

            <div className="mt-5 grid grid-cols-2 gap-3">
              <Field label="Floor" value={q.floor ? `$${q.floor}` : "—"} highlight />
              <Field label="Dividend weight" value={q.dividend_weight ?? "—"} />
              <Field label="Tier" value={q.meta_tier || q.tier_name || "—"} />
              <Field label="Generation" value={(q.attributes || []).find(a => (a.trait_type || "").toLowerCase() === "generation")?.value || "—"} />
            </div>

            <div className="mt-5 rounded-md border border-[#FFAB00]/40 bg-[#141008] p-4">
              <div className="text-[11px] uppercase tracking-widest text-stone-500">latest payout contribution</div>
              {loading ? (
                <div className="mt-2 flex items-center gap-2 text-stone-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> computing…</div>
              ) : attr?.latest_period ? (
                <>
                  <div className="mt-1 font-display text-3xl ember-text">{fmtUSD(attr.contribution_usd)}</div>
                  <div className="mt-1 text-xs text-stone-500 mono">
                    period {attr.latest_period} · ${attr.stock_track} pool {fmtUSD(attr.track_total_usd)} split across {attr.peers_on_track} terminal{attr.peers_on_track === 1 ? "" : "s"} on this floor
                  </div>
                </>
              ) : (
                <div className="mt-1 text-sm text-stone-500">no payouts logged yet · first distribution pending</div>
              )}
            </div>

            <a
              href={q.opensea_url}
              target="_blank"
              rel="noreferrer"
              data-testid="quotron-modal-opensea"
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#FFAB00] text-[#0A0906] font-semibold hover:bg-[#FFC940] transition-colors"
            >
              view on OpenSea <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div className="rounded-md border border-[#3A2E1C] bg-[#141008] p-3">
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`mt-1 mono text-sm ${highlight ? "text-[#4FFF4F]" : "text-stone-100"}`}>{String(value)}</div>
    </div>
  );
}

export default function QuotronGallery() {
  const [data, setData] = useState({ items: [], contract: "", collection_opensea: "" });
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get("/quotrons").then((r) => setData(r.data || { items: [] })).catch(() => {});
  }, []);
  const items = data.items || [];
  return (
    <section className="mt-6 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5" data-testid="quotron-gallery">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#FFAB00]">hardwired quotrons</div>
          <div className="font-display text-2xl mt-1">live inventory · Robinhood Chain</div>
          <div className="text-xs text-stone-500 mt-1 mono break-all">contract {data.contract}</div>
          {data.held_by && (
            <div className="text-xs mt-0.5 mono">
              <span className="text-stone-500">held by </span>
              <span className="text-[#4FFF4F]">{data.held_by.slice(0,6)}…{data.held_by.slice(-4)}</span>
            </div>
          )}
          <div className="text-xs text-stone-500 mt-0.5">tap a terminal to see its stock floor and latest payout contribution</div>
        </div>
        {data.collection_opensea && (
          <a
            href={data.collection_opensea}
            target="_blank"
            rel="noreferrer"
            data-testid="quotron-collection-link"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#3A2E1C] bg-[#1E1810] hover:border-[#FFAB00]/50 text-sm text-stone-200"
          >
            open collection on OpenSea <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-stone-500">no hardwired terminals yet · the fire's just lit</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((q) => <Thumb key={q.id} q={q} onClick={() => setActive(q)} />)}
        </div>
      )}
      <Modal q={active} onClose={() => setActive(null)} />
    </section>
  );
}
